import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { meetingStatusPresentation, formatMeetingDate } from './safety-ui.js';
import type { SafetyMeeting } from './use-safety-read-model.js';

interface SafetyMeetingHistoryProps {
  meetings: SafetyMeeting[];
  total: number;
  loading: boolean;
  error: string | null;
  hasAccess: boolean;
  onSelectMeeting: (id: string | number | null) => void;
}

export default function SafetyMeetingHistory({
  meetings,
  total,
  loading,
  error,
  hasAccess,
  onSelectMeeting,
}: SafetyMeetingHistoryProps) {
  const loadedCount = meetings.length;
  const trueTotal = typeof total === 'number' ? total : loadedCount;
  const isTruncated = trueTotal > loadedCount;
  if (!hasAccess) {
    return (
      <div style={LAYOUT.subPanel}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 8 }}>
          Safety meetings
        </div>
        <div style={{ color: C.textMuted, fontSize: 12 }}>
          Safety meeting access requires the safety_meetings permission.
        </div>
      </div>
    );
  }

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold }}>
          Safety meeting history
        </div>
        {!loading && (
          <div style={{ color: C.textMuted, fontSize: 12 }}>
            {isTruncated
              ? `Showing ${loadedCount} of ${trueTotal} meetings`
              : `${loadedCount} meeting${loadedCount !== 1 ? 's' : ''}`}
          </div>
        )}
      </div>

      {loading && <div style={{ color: C.textMuted, fontSize: 12 }}>Loading...</div>}
      {error && !loading && <div style={{ color: C.red, fontSize: 12 }}>{error}</div>}

      {!loading && !error && meetings.length === 0 && (
        <div style={{ color: C.textMuted, fontSize: 12 }}>No safety meetings on record for this scope.</div>
      )}

      {!loading && !error && meetings.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {meetings.map((meeting: SafetyMeeting) => {
            const status = meetingStatusPresentation(meeting.status ?? '');
            const sigCount = meeting.signature_count
              ?? meeting.safety_meeting_signatures?.length
              ?? 0;
            const duration = meeting.duration_minutes;
            return (
              <button
                key={meeting.id}
                type="button"
                onClick={() => onSelectMeeting(meeting.id ?? null)}
                style={{
                  padding: 12,
                  borderRadius: 10,
                  background: C.panel,
                  border: `1px solid ${C.borderSoft}`,
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'grid',
                  gap: 6,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>
                    Terminal {meeting.terminal_number || '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 999, color: status.color, background: status.background }}>
                      {status.label}
                    </span>
                    <span style={{ color: C.textMuted, fontSize: 11 }}>{sigCount} signed</span>
                  </div>
                </div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>
                  Topic: <span style={{ color: C.text }}>{meeting.topic || '—'}</span>
                </div>
                <div style={{ color: C.textMuted, fontSize: 11, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {meeting.opened_at && <span>Opened: {formatMeetingDate(meeting.opened_at)}</span>}
                  {meeting.closed_at && <span>Closed: {formatMeetingDate(meeting.closed_at)}</span>}
                  {typeof duration === 'number' && <span>{duration} min</span>}
                  {meeting.opened_by && <span>by {meeting.opened_by}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
